/*
 * NIUD LP cart drawer.
 * Loaded only by layout/LP-covers-drawer.liquid. Renders line items client-side
 * from /cart.js so the LP never has to pull in the theme's mini-cart stack.
 *
 * Listens for:
 *   niud:cart:added  — dispatched by niud-scripts.js when NIUD_CART_DRAWER is on
 *   niud:cart:error  — dispatched on a 422 from /cart/add.js
 */
(function () {
  'use strict';

  var drawer = document.getElementById('niud-drawer');
  var scrim = document.getElementById('niud-drawer-scrim');
  if (!drawer || !scrim) return;

  var itemsEl = drawer.querySelector('[data-drawer-items]');
  var emptyEl = drawer.querySelector('[data-drawer-empty]');
  var footerEl = drawer.querySelector('[data-drawer-footer]');
  var subtotalEl = drawer.querySelector('[data-drawer-subtotal]');
  var errorEl = drawer.querySelector('[data-drawer-error]');
  var closeBtn = drawer.querySelector('[data-drawer-close]');
  var shipEl = drawer.querySelector('[data-drawer-shipping]');
  var shipTextEl = drawer.querySelector('[data-drawer-shipping-text]');
  var shipFillEl = drawer.querySelector('[data-drawer-shipping-fill]');

  var moneyFormat = window.NIUD_MONEY_FORMAT || '${{amount}}';
  var freeShipCents = Number(window.NIUD_FREE_SHIP_CENTS || 0);
  var lastFocused = null;

  // ── money ───────────────────────────────────────────────────────────
  function formatMoney(cents) {
    var value = Number(cents || 0);

    function withDecimals(precision, thousands, decimal) {
      var num = (value / 100).toFixed(precision);
      var parts = num.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+$)/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, name) {
      switch (name) {
        case 'amount_no_decimals': return withDecimals(0, ',', '.');
        case 'amount_with_comma_separator': return withDecimals(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDecimals(0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDecimals(2, "'", '.');
        default: return withDecimals(2, ',', '.');
      }
    });
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // ── free shipping ───────────────────────────────────────────────────
  //
  // The threshold NUMBER cannot be read from Shopify: /cart/shipping_rates.json
  // only returns the free rate once you already qualify, so there is nothing to
  // read while the shopper is still short. It stays a theme setting.
  //
  // The QUALIFIED/NOT state can be read, and is authoritative — but the endpoint
  // demands a postal code. We only have valid probe zips for the two home
  // markets; everywhere else the probe is skipped and the bar falls back to the
  // configured threshold alone.
  var PROBE_ZIPS = { SG: '138588', MY: '50450' };
  var probeCache = {};

  function probeFreeShipping(cart) {
    var country = (window.Shopify && window.Shopify.country) || '';
    var zip = PROBE_ZIPS[country];
    if (!zip) return Promise.resolve(null);

    // Only worth asking near or above the threshold; below that we know it is not free.
    if (freeShipCents && cart.total_price < freeShipCents * 0.6) return Promise.resolve(false);

    var key = country + ':' + cart.total_price;
    if (key in probeCache) return Promise.resolve(probeCache[key]);

    var qs = 'shipping_address%5Bcountry%5D=' + encodeURIComponent(country) +
             '&shipping_address%5Bzip%5D=' + encodeURIComponent(zip);

    return fetch('/cart/shipping_rates.json?' + qs, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.shipping_rates) return null;
        var free = data.shipping_rates.some(function (rate) {
          return parseFloat(rate.price) === 0;
        });
        probeCache[key] = free;
        return free;
      })
      .catch(function () { return null; });
  }

  // Copy for the shipping progress bar. `state` is one of:
  //   'unlocked' — Shopify confirmed a free rate. Safe to promise.
  //   'almost'   — cart is over the configured threshold but Shopify says NOT
  //                free yet (the setting is out of sync). Must NOT promise.
  //   'away'     — still short. `remaining` is how much, in cents.
  // Returned string goes into innerHTML, so <strong> works.
  function freeShippingMessage(state, remaining, threshold) {
    if (state === 'unlocked') return 'You have unlocked free shipping.';
    if (state === 'almost') return 'Almost there — add a little more for free shipping.';
    return 'You are ' + formatMoney(remaining) + ' away from free shipping.';
  }

  function paintShipping(state, remaining, pct) {
    shipEl.hidden = false;
    shipEl.classList.toggle('niud-drawer__shipping--met', state === 'unlocked');
    if (shipTextEl) shipTextEl.innerHTML = freeShippingMessage(state, remaining, freeShipCents);
    if (shipFillEl) shipFillEl.style.width = pct + '%';
  }

  function renderShipping(cart) {
    if (!shipEl) return;

    if (cart.item_count === 0) {
      shipEl.hidden = true;
      return;
    }

    // The theme setting is keyed by currency; Shopify's real rule is keyed by
    // shipping zone. Those only agree in the two home markets, and those are
    // also the only markets we can probe. Anywhere else, show no bar rather
    // than promise a threshold we cannot verify.
    var country = (window.Shopify && window.Shopify.country) || '';
    if (!PROBE_ZIPS[country]) {
      shipEl.hidden = true;
      return;
    }

    // Paint immediately from the configured threshold so the drawer never waits
    // on a network call, then correct once Shopify answers.
    if (freeShipCents) {
      var remaining = Math.max(0, freeShipCents - cart.total_price);
      var pct = Math.min(100, (cart.total_price / freeShipCents) * 100);
      paintShipping(remaining <= 0 ? 'unlocked' : 'away', remaining, pct);
    } else {
      shipEl.hidden = true;
    }

    probeFreeShipping(cart).then(function (free) {
      if (free === null) return;              // unknown market — keep the setting-driven state
      var remaining = freeShipCents ? Math.max(0, freeShipCents - cart.total_price) : 0;

      if (free) {
        paintShipping('unlocked', 0, 100);
      } else if (freeShipCents && remaining <= 0) {
        // Setting says qualified, Shopify says no. Never claim free shipping.
        paintShipping('almost', 0, 95);
      }
    });
  }

  // ── render ──────────────────────────────────────────────────────────
  function itemHtml(item) {
    var img = item.image
      ? '<img class="niud-drawer__thumb" src="' + escapeHtml(item.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2')) + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy" width="68" height="68">'
      : '<span class="niud-drawer__thumb"></span>';

    var variant = item.variant_title
      ? '<p class="niud-drawer__item-variant">' + escapeHtml(item.variant_title) + '</p>'
      : '';

    return '' +
      '<li class="niud-drawer__item" data-key="' + escapeHtml(item.key) + '">' +
        img +
        '<div>' +
          '<p class="niud-drawer__item-name">' + escapeHtml(item.product_title) + '</p>' +
          variant +
          '<div class="niud-drawer__qty">' +
            '<button type="button" data-qty-step="-1" aria-label="Decrease quantity">&minus;</button>' +
            '<span class="niud-drawer__qty-value">' + item.quantity + '</span>' +
            '<button type="button" data-qty-step="1" aria-label="Increase quantity">+</button>' +
          '</div>' +
        '</div>' +
        '<div class="niud-drawer__item-right">' +
          '<span class="niud-drawer__item-price">' + formatMoney(item.final_line_price) + '</span>' +
          '<button type="button" class="niud-drawer__remove" data-remove>Remove</button>' +
        '</div>' +
      '</li>';
  }

  function render(cart) {
    var empty = cart.item_count === 0;

    if (itemsEl) itemsEl.innerHTML = empty ? '' : cart.items.map(itemHtml).join('');
    if (emptyEl) emptyEl.hidden = !empty;
    if (footerEl) footerEl.hidden = empty;
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    renderShipping(cart);

    var countEl = document.getElementById('niud-cart-count');
    if (countEl) {
      countEl.textContent = cart.item_count;
      countEl.style.display = cart.item_count > 0 ? 'flex' : 'none';
    }
  }

  function showError(message) {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function clearError() {
    if (errorEl) errorEl.hidden = true;
  }

  // ── data ────────────────────────────────────────────────────────────
  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function changeQuantity(key, quantity, row) {
    if (row) row.classList.add('niud-drawer__item--busy');
    clearError();

    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart.status && cart.status !== 200) {
          showError(cart.description || cart.message || 'Could not update your cart.');
          return fetchCart().then(render);
        }
        render(cart);
      })
      .catch(function () {
        showError('Could not update your cart. Please try again.');
        if (row) row.classList.remove('niud-drawer__item--busy');
      });
  }

  // ── open / close ────────────────────────────────────────────────────
  function open() {
    lastFocused = document.activeElement;
    scrim.classList.add('open');
    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    if (closeBtn) closeBtn.focus();
  }

  function close() {
    scrim.classList.remove('open');
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    clearError();
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  function openWithCart() {
    open();
    fetchCart().then(render).catch(function () {
      showError('Could not load your cart.');
    });
  }

  // ── events ──────────────────────────────────────────────────────────
  if (closeBtn) closeBtn.addEventListener('click', close);
  scrim.addEventListener('click', close);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawer.classList.contains('open')) close();
  });

  // Keep tab focus inside the drawer while it is open.
  drawer.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    var focusables = drawer.querySelectorAll('button, [href], input, select, textarea');
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  if (itemsEl) {
    itemsEl.addEventListener('click', function (e) {
      var row = e.target.closest('.niud-drawer__item');
      if (!row) return;
      var key = row.dataset.key;
      var current = Number(row.querySelector('.niud-drawer__qty-value').textContent) || 0;

      if (e.target.closest('[data-remove]')) {
        changeQuantity(key, 0, row);
        return;
      }

      var stepBtn = e.target.closest('[data-qty-step]');
      if (stepBtn) {
        changeQuantity(key, Math.max(0, current + Number(stepBtn.dataset.qtyStep)), row);
      }
    });
  }

  // Cart button in the layout opens the drawer instead of navigating to /cart.
  var cartBtn = document.getElementById('niud-cart-btn');
  if (cartBtn) {
    cartBtn.addEventListener('click', function (e) {
      e.preventDefault();
      openWithCart();
    });
  }

  document.addEventListener('niud:cart:added', function (e) {
    open();
    if (e.detail && e.detail.cart && e.detail.cart.items) {
      render(e.detail.cart);
    } else {
      fetchCart().then(render);
    }
  });

  document.addEventListener('niud:cart:error', function (e) {
    openWithCart();
    showError((e.detail && e.detail.message) || 'That item could not be added.');
  });

  window.NIUD = window.NIUD || {};
  window.NIUD.openCartDrawer = openWithCart;

  // Prime the badge without opening anything.
  (window.requestIdleCallback || function (c) { setTimeout(c, 1); })(function () {
    fetchCart().then(function (cart) {
      var countEl = document.getElementById('niud-cart-count');
      if (countEl && cart.item_count > 0) {
        countEl.textContent = cart.item_count;
        countEl.style.display = 'flex';
      }
    });
  });
})();
